
import React from 'react';

import { ImageLibraryItem } from '@/hooks/runware/image-library';
import { useFilterName, useFilterNames } from '@/hooks/runware/image-library/use-filter-names';
import GlassPill from '@/components/ui/GlassPill';

interface FilterInfoDisplayProps {
  image: ImageLibraryItem;
}

export const FilterInfoDisplay: React.FC<FilterInfoDisplayProps> = ({ image }) => {
  // Fetch individual filter names
  const { data: styleName } = useFilterName(image.style_id);
  const { data: influenceName } = useFilterName(image.influence_id);
  const { data: backgroundName } = useFilterName(image.background_id);
  
  // Fetch multiple filter names
  const { data: environmentNames } = useFilterNames(image.environment_ids);
  const { data: moodNames } = useFilterNames(image.mood_ids);
  const { data: colorNames } = useFilterNames(image.color_ids);

  // Helper to check if there are any filters
  const hasAnyFilter = !!(
    image.image_type ||
    image.style_id ||
    image.influence_id ||
    image.background_id ||
    (image.environment_ids && image.environment_ids.length > 0) ||
    (image.mood_ids && image.mood_ids.length > 0) ||
    (image.color_ids && image.color_ids.length > 0)
  );
  
  if (!hasAnyFilter) {
    return (
      <div className="text-sm text-center text-muted-foreground py-2">
        No filters applied to this image
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {/* Image Type */}
      {image.image_type && (
        <GlassPill type={image.image_type} variant="documentation" />
      )}
      
      {/* Style */}
      {image.style_id && styleName && (
        <GlassPill type={styleName} variant="blue" />
      )}
      
      {/* Influence */}
      {image.influence_id && influenceName && (
        <GlassPill type={influenceName} variant="purple" />
      )}
      
      {/* Background */}
      {image.background_id && backgroundName && (
        <GlassPill type={backgroundName} variant="amber" />
      )}
      
      {/* Environment - multiple */}
      {image.environment_ids && image.environment_ids.length > 0 && 
        image.environment_ids.map(id => (
          <GlassPill key={id} type={environmentNames?.[id] || "Loading..."} variant="green" />
        ))
      }
      
      {/* Mood - multiple */}
      {image.mood_ids && image.mood_ids.length > 0 && 
        image.mood_ids.map(id => (
          <GlassPill key={id} type={moodNames?.[id] || "Loading..."} variant="video" />
        ))
      }
      
      {/* Color - multiple */}
      {image.color_ids && image.color_ids.length > 0 && 
        image.color_ids.map(id => (
          <GlassPill key={id} type={colorNames?.[id] || "Loading..."} variant="link" />
        ))
      }
      
      {/* End of filter pills */}
    </div>
  );
};
